import type { AsteriskCallEvent } from './asterisk-notify.service';
import type { CallType, WorkplaceCall, WorkplaceClientResponse } from './workplace.service';

/**
 * The rule of the call of the operator (stage 4, design §3.2 + the decisions of the PM B2 / S2 / S3), as a PURE reducer: no Angular, no timers, no HTTP — `reduce(state, action, now)` returns a new state and
 * never touches the old one. `now` is the monotonic clock of the browser (`performance.now()`, ms); the clock of the exchange (`date` of an event) is not read at all — the phases never go back, which is all the
 * order of the events needs. `CallStateService` is the shell around it: it feeds the actions (the bus, W1, W2, the router) and does what the new state asks (timers, requests, the notification).
 *
 * The words:
 *  - `held` — the call of the workplace: the one the workplace opens, the band under the header is about;
 *  - `pending` — a call that came while the operator is busy: an order of `held` is open (see {@link orderOpen}), or `held` is talking and the new one only rings. It becomes `held` when the operator is free: the conversation of `held`
 *    ends, or — the owner's word, B2′ — the operator EXPLICITLY closes the order that kept the switch (`closed`: «Создать заказ», «Снять бронь», «Сохранить изменения» / «Отмена» of a change) or the call itself
 *    («Закрыть звонок», `close` — the owner, 21.10: «оператор явно сохраняет либо отменяет заказ или звонок, только после этого следующий звонок автоматом активизируется»). A booking that merely ran out is not an
 *    explicit close, and a conversation with no order open at all still holds the workplace until it ends (B2);
 *  - `previous` — the `held` that a newer call pushed away. A call that ends without the operator's own `talking` (B2: it rang at several operators, another one answered) is dropped, and `previous` comes back.
 */

export type CallPhase = 'ringing' | 'talking' | 'finished';

/** What is known of the client behind a call: W2 is on its way / answered with a client / with nobody / failed. */
export type ClientLoad =
  | { readonly status: 'loading' }
  | { readonly status: 'found'; readonly lookup: WorkplaceClientResponse; /** Finished orders (the summary of «Архива») plus the ones in work — «15 заказов». */ readonly ordersTotal?: number }
  | { readonly status: 'none'; readonly lookup: WorkplaceClientResponse }
  | { readonly status: 'error' };

/** A reservation of the call that is alive: `until` is the moment (`performance.now()` ms) its term runs out. */
export interface LiveCart {
  readonly id: number;
  readonly until: number;
  /** The first line of the reservation — «Бронь Медовый max привязана к этому звонку». */
  readonly title?: string;
}

export interface ShellCall {
  readonly callId: string;
  /** Digits, as the event has them. */
  readonly phone: string;
  readonly mobile: boolean;
  readonly countryId?: number;
  readonly phase: CallPhase;
  /** The operator's OWN `talking` was seen (an event, or W1 saying so). Without it a call that ends is not the operator's. */
  readonly talked: boolean;
  /** From W1 — the event has none. */
  readonly type?: CallType;
  /** `performance.now()` of the moment the call began to ring (W1's `ringSeconds` corrects it). */
  readonly ringAt: number;
  readonly talkAt?: number;
  readonly endAt?: number;
  /** The first time this browser saw the call. */
  readonly seenAt: number;
  /** Every reservation the call is known to hold (W1, the address of step 2 of the editor). */
  readonly carts: readonly number[];
  /** The reservations of the call that are alive (`/orders-current/rows`): a live one keeps the order of the call open. */
  readonly liveCarts: readonly LiveCart[];
  /**
   * The booking of the call ran out by itself — the server dropped it when its term was up (10 minutes from the last action in the cart), not «Создать заказ» / «Снять бронь». A finished
   * call whose booking ran out leaves the workplace (the owner, 21.10: «по истечению брони открывается следующий звонок или возвращается в исходную позицию Рабочее место»).
   */
  readonly lapsed?: boolean;
  readonly client: ClientLoad;
  /** «Скрыть» on the band of a finished call. */
  readonly bandHidden: boolean;
  /** When the call is to be read from W1 next (`performance.now()` ms); absent — never. */
  readonly nextSync?: number;
  readonly syncTries: number;
}

/** The editor is open for the call of the workplace: a reservation on step 2 (`cart`) or a change of an order (`edit`). Step 1 without a reservation holds nothing — the draft stays, the order is not open. */
export interface EditorOpen {
  readonly callId: string;
  readonly kind: 'cart' | 'edit';
  readonly cartId?: number;
  readonly orderId?: number;
}

export interface CallShellState {
  /** The number of the operator (`auth.user.asteriskId`), empty/absent — none picked. */
  readonly slot?: string;
  /** The operator's groups have numbers to pick from (`GET /callcenter/operator-ids` is not empty). */
  readonly slotsAvailable: boolean;
  readonly busOpen: boolean;
  readonly held?: ShellCall;
  readonly pending?: ShellCall;
  readonly previous?: ShellCall;
  readonly editor?: EditorOpen;
}

export const INITIAL_CALL_STATE: CallShellState = { slotsAvailable: false, busOpen: false };

export type CallAction =
  /** A message of `operator.N`. */
  | { readonly type: 'event'; readonly event: AsteriskCallEvent }
  /** W1 with the id of a call the shell knows. `call: null` — the server has no such call of the operator (yet). */
  | { readonly type: 'server'; readonly callId: string; readonly call: WorkplaceCall | null }
  /** W1 without an id: the call the operator's number has right now (the start, the bus coming back, the tab coming back). */
  | { readonly type: 'current'; readonly call: WorkplaceCall | null }
  /** W1 by id failed (network, server): the next try waits. */
  | { readonly type: 'syncFailed'; readonly callId: string }
  /**
   * The finished call this workplace was about before the page was reloaded (or opened in another tab), read again from W1 by its id, with its reservations that are still alive: the workplace goes on
   * where it stopped (the owner, 24.10: «когда перезагружаешь в таких случаях продолжаем оттуда где остановились»). A call with nothing alive, or a workplace that already holds a call, is left as it is.
   */
  | { readonly type: 'resumed'; readonly call: WorkplaceCall; readonly live: readonly LiveCart[] }
  | { readonly type: 'client'; readonly callId: string; readonly client: ClientLoad }
  /** `/orders-current/rows` of the reservations of the call: the ones still alive (the rest was made an order, released, ran out). */
  | { readonly type: 'carts'; readonly callId: string; readonly live: readonly LiveCart[] }
  /** The address of step 2 of the editor named a reservation of the call. */
  | { readonly type: 'cartSeen'; readonly callId: string; readonly cartId: number }
  | { readonly type: 'editor'; readonly editor?: EditorOpen }
  | { readonly type: 'hide'; readonly callId: string }
  /** «Закрыть звонок» — a finished call with no order open goes, the workplace is «без звонка». */
  | { readonly type: 'close' }
  /**
   * B2′: the operator EXPLICITLY closed an order of the held call — «Создать заказ» (the booking became the order `orderId`), «Снять бронь» (the booking `orderId` was released), «Сохранить изменения» / «Отмена» / leaving the change of the order
   * `orderId`. A waiting call opens at once, though the held one is still talking. A booking that ran out is NOT this action (nobody did anything), and an order that is not the held call's changes nothing.
   */
  | { readonly type: 'closed'; readonly orderId: number }
  | { readonly type: 'slot'; readonly slot?: string }
  | { readonly type: 'slots'; readonly available: boolean }
  | { readonly type: 'bus'; readonly open: boolean }
  /** Time passed (a reservation may have run out). */
  | { readonly type: 'tick' };

// ---------- the numbers ----------

/** The first read of W1 after a call appeared: the exchange writes the call to the database after it pushed the event. */
export const FIRST_SYNC_MS = 1500;
/** The second one, when the first had no `type` yet. */
export const SECOND_SYNC_MS = 4000;
/** While it rings (the call may have been taken at another number and this one then hears nothing more) — up to {@link RINGING_SYNC_LIMIT_MS}. */
export const RINGING_SYNC_MS = 20_000;
export const RINGING_SYNC_LIMIT_MS = 20 * 60_000;
/** While it talks (a `finished` may have been lost). */
export const TALKING_SYNC_MS = 60_000;
/** A finished call stays on the band for so long (or until «Скрыть», or until the next call). */
export const FINISHED_BAND_MS = 30 * 60_000;
/** `app.cart.expire_seconds`: a reservation just seen in an address lives so long until `/rows` says how much. */
export const CART_TERM_MS = 600_000;
/** A reservation `/rows` no longer has is one that ran out if its term was up by then (give or take the clocks): one that goes sooner was made an order or released. */
export const CART_LAPSE_GRACE_MS = 5_000;
/** A call that W1 does not know (no `current` answer for it) is believed gone only after so long: the database lags behind the push. */
export const SERVER_NONE_GRACE_MS = 15_000;

/** The id of a call is the id of the exchange; a longer one (or not a string at all) is not an event of the exchange — the intake of the bus has no authorisation of its own (S1), so what comes is checked here. */
export const MAX_CALL_ID_LENGTH = 64;

const RANK: Record<CallPhase, number> = { ringing: 0, talking: 1, finished: 2 };

type Slot = 'held' | 'pending' | 'previous';

// ---------- queries ----------

export function isCallPhase(value: unknown): value is CallPhase {
  return value === 'ringing' || value === 'talking' || value === 'finished';
}

/**
 * An order is open for the call of the workplace (the decision of the owner: a new call is taken only when the current order is closed): the editor is open FOR THIS CALL (a change of an order, or step 2 with a
 * reservation), or the call has a reservation of the operator that is alive. Step 1 without a reservation holds nothing.
 */
export function orderOpen(state: CallShellState, now: number): boolean {
  const held = state.held;
  if (!held) {
    return false;
  }
  return state.editor?.callId === held.callId || held.liveCarts.some(cart => cart.until > now);
}

/** The soonest moment a call is to be read from W1. */
export function nextSyncOf(state: CallShellState): { callId: string; at: number } | undefined {
  const due = [state.held, state.pending]
    .filter((call): call is ShellCall => !!call && call.nextSync !== undefined)
    .map(call => ({ callId: call.callId, at: call.nextSync as number }));
  return due.length ? due.reduce((a, b) => (b.at < a.at ? b : a)) : undefined;
}

/** The soonest moment a live reservation of the call of the workplace runs out (the pending call may become the held one then). */
export function nextCartEndOf(state: CallShellState, now: number): number | undefined {
  const ends = (state.held?.liveCarts ?? []).map(cart => cart.until).filter(until => until > now);
  return ends.length ? Math.min(...ends) : undefined;
}

export function callOf(state: CallShellState, callId: string): ShellCall | undefined {
  const slot = locate(state, callId);
  return slot ? state[slot] : undefined;
}

// ---------- the reducer ----------

export function reduce(state: CallShellState, action: CallAction, now: number): CallShellState {
  return normalize(step(state, action, now), now);
}

function step(state: CallShellState, action: CallAction, now: number): CallShellState {
  switch (action.type) {
    case 'event':
      return onEvent(state, action.event, now);
    case 'server':
      return onServer(state, action.callId, action.call, now);
    case 'current':
      return onCurrent(state, action.call, now);
    case 'syncFailed':
      return update(state, action.callId, call => scheduled(call, now));
    case 'resumed':
      return onResumed(state, action.call, action.live, now);
    case 'client':
      return update(state, action.callId, call => ({ ...call, client: action.client }));
    case 'carts':
      return update(state, action.callId, call => {
        // a booking that is gone when its term was up ran out; one that is gone sooner was made an order or released — the operator's own word
        // (after the conversation only: a booking that ran out while the client was still on the line does not take the call away when he hangs up)
        const ranOut = call.phase === 'finished' && action.live.length === 0
          && call.liveCarts.some(cart => cart.until <= now + CART_LAPSE_GRACE_MS && !action.live.some(l => l.id === cart.id));
        const { lapsed: _was, ...rest } = call;
        return {
          ...rest,
          liveCarts: action.live,
          carts: unite(call.carts, action.live.map(cart => cart.id)),
          ...(ranOut || (call.lapsed && action.live.length === 0) ? { lapsed: true } : {}),
        };
      });
    case 'cartSeen':
      return update(state, action.callId, call => ({
        ...call,
        carts: unite(call.carts, [action.cartId]),
        liveCarts: call.liveCarts.some(cart => cart.id === action.cartId) ? call.liveCarts : [...call.liveCarts, { id: action.cartId, until: now + CART_TERM_MS }],
      }));
    case 'editor':
      return { ...state, editor: action.editor };
    case 'hide':
      return update(state, action.callId, call => ({ ...call, bandHidden: true }));
    case 'close':
      // «Закрыть звонок» is the operator's own word: a finished call goes even with a live booking (the booking stays in «Заказы → Корзины» for its term) — only the editor open for this call keeps it
      return state.held?.phase === 'finished' && state.editor?.callId !== state.held.callId ? { ...state, held: undefined, previous: undefined, editor: undefined } : state;
    case 'closed':
      return onClosed(state, action.orderId, now);
    case 'slot':
      // another number — other calls: nothing of the old number's is kept
      return { ...state, slot: action.slot || undefined, held: undefined, pending: undefined, previous: undefined, editor: undefined };
    case 'slots':
      return { ...state, slotsAvailable: action.available };
    case 'bus':
      return { ...state, busOpen: action.open };
    case 'tick':
      return state;
  }
}

/**
 * B2′: an order of the held call was closed by the operator himself. The order is the held call's if the call holds the reservation (`carts`: W1, the address of step 2) or the editor is open for it (a change of that order, step 2 of that
 * reservation). Only then a waiting call takes the place — the one that was held is pushed to `previous` (it goes on talking on the line, but the workplace is not about it any more), and nothing of its editor is left open.
 */
function onClosed(state: CallShellState, orderId: number, now: number): CallShellState {
  const { held, pending, editor } = state;
  if (!held || !pending) {
    return state;
  }
  const editorIsThis = editor?.callId === held.callId && (editor.cartId === orderId || editor.orderId === orderId);
  if (!held.carts.includes(orderId) && !editorIsThis) {
    return state;
  }
  if (held.liveCarts.some(cart => cart.id !== orderId && cart.until > now)) {
    // the call holds another live booking: that order is open still, and it is what keeps the switch
    return state;
  }
  return { ...state, held: pending, previous: held, pending: undefined, editor: undefined };
}

/** What follows from any change: a pending call is taken once the operator is free (the end of the conversation of the held one, or its order closed — see `onClosed` for the explicit close of an order of a conversation that goes on). */
function normalize(state: CallShellState, now: number): CallShellState {
  let next = state;
  if (next.editor && next.editor.callId !== next.held?.callId) {
    next = { ...next, editor: undefined };
  }
  const pending = next.pending;
  if (pending && !orderOpen(next, now) && (next.held?.phase !== 'talking' || pending.phase === 'talking')) {
    next = { ...next, held: pending, previous: next.held, pending: undefined, editor: undefined };
  } else if (!pending && next.held?.phase === 'finished' && next.held.lapsed && !orderOpen(next, now)) {
    // the conversation is over and its booking ran out: the workplace is back where it starts — «Ждём звонка»
    next = { ...next, held: undefined, previous: undefined, editor: undefined };
  }
  return next;
}

// ---------- the bus ----------

function onEvent(state: CallShellState, ev: AsteriskCallEvent, now: number): CallShellState {
  const phase: unknown = ev.event;
  // the fields come from the bus as they are: the types are checked, not trusted
  const callId: unknown = ev.callId;
  const clientPhone: unknown = ev.clientPhone;
  if (!isCallPhase(phase) || typeof clientPhone !== 'string' || !digitsOf(clientPhone) || typeof callId !== 'string' || !callId || callId.length > MAX_CALL_ID_LENGTH) {
    return state;
  }
  const slot = locate(state, ev.callId);
  if (!slot) {
    // `finished` of a call nobody knows is nothing; `talking` first is a call opened in the middle (the bus was down): a call in the phase it is in
    return phase === 'finished' ? state : place(state, newCall(ev, phase, now), now);
  }
  const call = state[slot] as ShellCall;
  if (RANK[phase] <= RANK[call.phase]) {
    // a repeat, or a late message of an earlier phase
    return state;
  }
  const advanced = advance(call, phase, now);
  if (slot === 'previous' && phase === 'talking') {
    // the call the newer one pushed away was answered after all: it is the operator's again, the newer one waits
    const newer = state.held;
    return { ...state, held: advanced, previous: undefined, pending: newer && newer.phase !== 'finished' ? newer : state.pending, editor: undefined };
  }
  return settle(state, slot, advanced);
}

function newCall(ev: AsteriskCallEvent, phase: CallPhase, now: number): ShellCall {
  return {
    callId: ev.callId,
    phone: digitsOf(ev.clientPhone),
    mobile: !!ev.mobile,
    countryId: typeof ev.country?.id === 'number' ? ev.country.id : undefined,
    phase,
    talked: phase === 'talking',
    ringAt: now,
    talkAt: phase === 'talking' ? now : undefined,
    seenAt: now,
    carts: [],
    liveCarts: [],
    client: { status: 'loading' },
    bandHidden: false,
    nextSync: now + FIRST_SYNC_MS,
    syncTries: 0,
  };
}

function advance(call: ShellCall, phase: CallPhase, now: number): ShellCall {
  return {
    ...call,
    phase,
    talked: call.talked || phase === 'talking',
    talkAt: phase === 'talking' ? now : call.talkAt,
    endAt: phase === 'finished' ? now : call.endAt,
    nextSync: phase === 'finished' ? undefined : call.nextSync,
  };
}

/** Where a call that is new to the shell goes. */
function place(state: CallShellState, call: ShellCall, now: number): CallShellState {
  const held = state.held;
  if (!held) {
    return { ...state, held: call };
  }
  if (orderOpen(state, now)) {
    // the order of the held call is not finished: the new call waits (a newer pending call replaces it)
    return { ...state, pending: call };
  }
  if (call.phase === 'ringing' && held.phase === 'talking') {
    // B2: a call that only rings does not take the place of a conversation (it takes it when the conversation ends or the operator closes the order by himself — `closed`, B2′)
    return { ...state, pending: call };
  }
  return { ...state, held: call, previous: held, editor: undefined };
}

/**
 * Puts a changed call back where it was. A call that ENDED without the operator's own `talking` is not the operator's (it rang at several operators, another one answered; a call that rang and was dropped):
 * it goes, and the call it had pushed away comes back — no band, no card for it.
 */
function settle(state: CallShellState, slot: Slot, call: ShellCall): CallShellState {
  if (call.phase === 'finished' && !call.talked) {
    if (slot === 'pending') {
      return { ...state, pending: undefined };
    }
    if (slot === 'previous') {
      return { ...state, previous: undefined };
    }
    return { ...state, held: state.previous, previous: undefined, editor: undefined };
  }
  switch (slot) {
    case 'held':
      return { ...state, held: call };
    case 'pending':
      return { ...state, pending: call };
    case 'previous':
      return { ...state, previous: call };
  }
}

// ---------- the server (W1) ----------

function onServer(state: CallShellState, callId: string, wc: WorkplaceCall | null, now: number): CallShellState {
  const slot = locate(state, callId);
  if (!slot) {
    return state;
  }
  const call = state[slot] as ShellCall;
  if (!wc) {
    // not the operator's, or not in the database yet: a try is spent
    return put(state, slot, scheduled(call, now));
  }
  const phase = wc.state;
  // the phase first (a conversation the events did not tell of), then the exact stamps over it
  let next: ShellCall = isCallPhase(phase) && RANK[phase] > RANK[call.phase] ? advance(call, phase, now) : call;
  next = {
    ...next,
    type: wc.type ?? next.type,
    mobile: next.mobile || wc.mobile,
    countryId: next.countryId ?? wc.countryId,
    carts: unite(next.carts, wc.cartIds ?? []),
    ringAt: next.phase === 'ringing' && wc.ringSeconds != null ? now - wc.ringSeconds * 1000 : next.ringAt,
    talkAt: wc.talkSeconds != null ? now - wc.talkSeconds * 1000 : next.talkAt,
    endAt: wc.endedSecondsAgo != null ? now - wc.endedSecondsAgo * 1000 : next.endAt,
  };
  if (next.endAt !== undefined && wc.talkDurationSeconds != null) {
    next = { ...next, talkAt: next.endAt - wc.talkDurationSeconds * 1000 };
  }
  if (wc.elsewhere && !next.talked) {
    // taken at another number: this one hears no `finished`
    return settle(state, slot, { ...next, phase: 'finished', endAt: next.endAt ?? now });
  }
  return settle(state, slot, scheduled(next, now));
}

function onCurrent(state: CallShellState, wc: WorkplaceCall | null, now: number): CallShellState {
  if (wc) {
    if (locate(state, wc.callId)) {
      return onServer(state, wc.callId, wc, now);
    }
    if (wc.state === 'finished' || wc.elsewhere || !digitsOf(wc.phone)) {
      return state;
    }
    const call: ShellCall = {
      callId: wc.callId,
      phone: digitsOf(wc.phone),
      mobile: wc.mobile,
      countryId: wc.countryId,
      phase: wc.state,
      talked: wc.state === 'talking',
      type: wc.type ?? undefined,
      ringAt: now - (wc.ringSeconds ?? 0) * 1000,
      talkAt: wc.talkSeconds != null ? now - wc.talkSeconds * 1000 : wc.state === 'talking' ? now : undefined,
      seenAt: now,
      carts: unite([], wc.cartIds ?? []),
      liveCarts: [],
      client: { status: 'loading' },
      bandHidden: false,
      nextSync: now + FIRST_SYNC_MS,
      syncTries: 0,
    };
    return place(state, call, now);
  }
  // the server has no current call: a call the shell believes is going has ended (a `finished` was lost) — unless it is young enough for the database not to have it yet
  let next = state;
  for (const slot of ['held', 'pending'] as const) {
    const call = next[slot];
    if (call && call.phase !== 'finished' && now - call.seenAt > SERVER_NONE_GRACE_MS) {
      next = settle(next, slot, advance(call, 'finished', now));
    }
  }
  return next;
}

/** A finished call of this workplace comes back after a reload (see the action `resumed`): the conversation is over, its booking is alive — the card of the finished call, as it was. */
function onResumed(state: CallShellState, wc: WorkplaceCall, live: readonly LiveCart[], now: number): CallShellState {
  if (state.held || locate(state, wc.callId) || wc.state !== 'finished' || wc.elsewhere || !digitsOf(wc.phone) || live.length === 0) {
    return state;
  }
  const endAt = now - (wc.endedSecondsAgo ?? 0) * 1000;
  const talkAt = wc.talkDurationSeconds != null ? endAt - wc.talkDurationSeconds * 1000 : undefined;
  const call: ShellCall = {
    callId: wc.callId,
    phone: digitsOf(wc.phone),
    mobile: wc.mobile,
    countryId: wc.countryId,
    phase: 'finished',
    // a booking of the call was made by this operator: the call was his
    talked: true,
    type: wc.type ?? undefined,
    ringAt: talkAt ?? endAt,
    talkAt,
    endAt,
    seenAt: now,
    carts: unite(wc.cartIds ?? [], live.map(cart => cart.id)),
    liveCarts: live,
    client: { status: 'loading' },
    bandHidden: false,
    syncTries: 0,
  };
  return { ...state, held: call };
}

/** The next read of a call from W1, after one was spent. */
function scheduled(call: ShellCall, now: number): ShellCall {
  const tries = call.syncTries + 1;
  let nextSync: number | undefined;
  if (call.phase === 'finished') {
    nextSync = undefined;
  } else if (!call.type && tries < 2) {
    nextSync = now + SECOND_SYNC_MS;
  } else if (call.phase === 'ringing') {
    nextSync = now - call.seenAt < RINGING_SYNC_LIMIT_MS ? now + RINGING_SYNC_MS : undefined;
  } else {
    nextSync = now + TALKING_SYNC_MS;
  }
  return { ...call, syncTries: tries, nextSync };
}

// ---------- helpers ----------

function locate(state: CallShellState, callId: string): Slot | undefined {
  return (['held', 'pending', 'previous'] as const).find(slot => state[slot]?.callId === callId);
}

function put(state: CallShellState, slot: Slot, call: ShellCall): CallShellState {
  switch (slot) {
    case 'held':
      return { ...state, held: call };
    case 'pending':
      return { ...state, pending: call };
    case 'previous':
      return { ...state, previous: call };
  }
}

function update(state: CallShellState, callId: string, change: (call: ShellCall) => ShellCall): CallShellState {
  const slot = locate(state, callId);
  return slot ? put(state, slot, change(state[slot] as ShellCall)) : state;
}

function unite(known: readonly number[], more: readonly number[]): readonly number[] {
  const added = more.filter(id => !known.includes(id));
  return added.length ? [...known, ...added] : known;
}

export function digitsOf(value: string | undefined | null): string {
  return (value ?? '').replace(/\D/g, '');
}
